import { Component, inject } from '@angular/core';
import { ActivatedRouteSnapshot, NavigationEnd, Router, RouterModule } from '@angular/router';
import { BehaviorSubject, merge } from 'rxjs';
import { filter } from 'rxjs/operators';
import { CommonModule } from '@angular/common';
import { RippleModule } from 'primeng/ripple';
import { ButtonModule } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';
import { CatalogDetailBreadcrumbService } from '@/app/layout/catalog-detail-breadcrumb.service';
import { WorkflowDetailBreadcrumbService } from '@/app/layout/workflow-detail-breadcrumb.service';
import { RequestDetailBreadcrumbService } from '@/app/layout/request-detail-breadcrumb.service';
import { AdaptersService } from '@/app/pages/adapters/adapters.service';
import { AuthService } from '@/app/auth/auth.service';

interface Breadcrumb {
    label: string;
    url?: string;
}

@Component({
    selector: '[app-breadcrumb]',
    standalone: true,
    imports: [CommonModule, RouterModule, ButtonModule, RippleModule, TooltipModule],
    template: `
        <nav class="layout-breadcrumb">
            <ol>
                <li><i class="pi pi-home"></i></li>
                <ng-template ngFor let-item let-last="last" [ngForOf]="breadcrumbs$ | async">
                    <li><i class="pi pi-angle-right"></i></li>
                    <li>
                        @if (item.url) {
                            <a [routerLink]="item.url" class="layout-breadcrumb-link">{{ item.label }}</a>
                        } @else {
                            <span>{{ item.label }}</span>
                        }
                    </li>
                </ng-template>
            </ol>
        </nav>
        <div class="layout-breadcrumb-buttons">
            <button pButton pRipple type="button" icon="pi pi-cloud-upload" class="p-button-rounded p-button-text p-button-plain"></button>
            <button pButton pRipple type="button" icon="pi pi-bookmark" class="p-button-rounded p-button-text p-button-plain"></button>
            <button
                pButton
                pRipple
                type="button"
                icon="pi pi-power-off"
                class="p-button-rounded p-button-text p-button-plain"
                pTooltip="Logout"
                tooltipPosition="bottom"
                (click)="logout()"
            ></button>
        </div>
    `,
    host: {
        class: 'layout-breadcrumb-container'
    }
})
export class AppBreadcrumb {
    private readonly router = inject(Router);
    private readonly auth = inject(AuthService);
    private readonly catalogDetailLabel = inject(CatalogDetailBreadcrumbService);
    private readonly workflowDetailLabel = inject(WorkflowDetailBreadcrumbService);
    private readonly requestDetailLabel = inject(RequestDetailBreadcrumbService);
    private readonly adaptersSvc = inject(AdaptersService);

    private readonly _breadcrumbs$ = new BehaviorSubject<Breadcrumb[]>([]);

    readonly breadcrumbs$ = this._breadcrumbs$.asObservable();

    constructor() {
        merge(
            this.router.events.pipe(filter((event) => event instanceof NavigationEnd)),
            this.catalogDetailLabel.label$,
            this.workflowDetailLabel.label$,
            this.requestDetailLabel.label$
        ).subscribe(() => {
            this.rebuildBreadcrumbs();
        });
    }

    private rebuildBreadcrumbs(): void {
        const path = this.router.url.split('?')[0].replace(/\/$/, '') || '/';
        const regimenDetail = /^\/regimens\/([^/]+)$/.exec(path);
        if (regimenDetail) {
            const id = regimenDetail[1];
            const regimen = this.catalogDetailLabel.getRegimenLabel();
            this._breadcrumbs$.next([
                { label: 'Regimens', url: '/regimens' },
                { label: regimen ?? id, url: path }
            ]);
            return;
        }

        // Clinical Validation Workspace — same "Regimens >> {name}" trail as regimen detail.
        const clinicalValidation = /^\/clinical-validation\/([^/]+)$/.exec(path);
        if (clinicalValidation) {
            const regimen = this.catalogDetailLabel.getRegimenLabel();
            this._breadcrumbs$.next([
                { label: 'Regimens', url: '/regimens' },
                { label: regimen ?? 'Clinical Validation', url: path }
            ]);
            return;
        }

        if (/^\/adapters\/schema$/.exec(path)) {
            this._breadcrumbs$.next([
                { label: 'Adapters', url: '/adapters' },
                { label: 'CBCM Schema', url: path }
            ]);
            return;
        }

        const adapterEdit = /^\/adapters\/([^/]+)\/edit$/.exec(path);
        if (adapterEdit) {
            const id = adapterEdit[1];
            const name = this.adaptersSvc.getAdapterNameSnapshot(id);
            this._breadcrumbs$.next([
                { label: 'Adapters', url: '/adapters' },
                { label: name ?? id },
                { label: 'Transformation Studio' }
            ]);
            return;
        }

        const adapterHistory = /^\/adapters\/([^/]+)\/history$/.exec(path);
        if (adapterHistory) {
            const id = adapterHistory[1];
            const name = this.adaptersSvc.getAdapterNameSnapshot(id);
            this._breadcrumbs$.next([
                { label: 'Adapters', url: '/adapters' },
                { label: name ?? id, url: `/adapters/${id}/edit` },
                { label: 'Version history' }
            ]);
            return;
        }

        const workflowDetail = /^\/workflows-old\/([^/]+)(\/activity)?$/.exec(path);
        if (workflowDetail) {
            const id = workflowDetail[1];
            const name = this.workflowDetailLabel.getWorkflowLabel();
            const isActivity = !!workflowDetail[2];
            const detailCrumbs: Breadcrumb[] = [
                { label: 'Workflows (legacy)', url: '/workflows-old' },
                { label: name ?? id, url: `/workflows-old/${id}` }
            ];
            if (isActivity) {
                detailCrumbs.push({ label: 'Active protocols' });
            }
            this._breadcrumbs$.next(detailCrumbs);
            return;
        }

        const requestDetail = /^\/requests\/([^/]+)$/.exec(path);
        if (requestDetail) {
            const id = requestDetail[1];
            const name = this.requestDetailLabel.getRequestLabel();
            this._breadcrumbs$.next([
                { label: 'Requests', url: '/requests' },
                { label: name ?? id, url: path }
            ]);
            return;
        }

        const root = this.router.routerState.snapshot.root;
        const breadcrumbs: Breadcrumb[] = [];
        this.addBreadcrumb(root, [], breadcrumbs);
        this._breadcrumbs$.next(breadcrumbs);
    }

    private addBreadcrumb(route: ActivatedRouteSnapshot, parentUrl: string[], breadcrumbs: Breadcrumb[]) {
        const routeUrl = parentUrl.concat(route.url.map((url) => url.path));
        const breadcrumb = route.data['breadcrumb'];
        const parentBreadcrumb = route.parent && route.parent.data ? route.parent.data['breadcrumb'] : null;

        if (breadcrumb && breadcrumb !== parentBreadcrumb) {
            breadcrumbs.push({
                label: route.data['breadcrumb'],
                url: '/' + routeUrl.join('/')
            });
        }

        if (route.firstChild) {
            this.addBreadcrumb(route.firstChild, routeUrl, breadcrumbs);
        }
    }

    logout(): void {
        this.auth.logout().subscribe({
            next: () => void this.router.navigate(['/login']),
            error: () => void this.router.navigate(['/login'])
        });
    }
}
